/**
 * Contratos da API da Fase 1 (identidade e plataforma).
 * O mesmo schema valida o formulário no frontend e a requisição na API.
 */
import { z } from "zod";
import { isValidCnpj, onlyDigits } from "./documents";

const email = z.string().trim().toLowerCase().email("E-mail inválido").max(254);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();
const optionalDigits = (len: number, label: string) =>
  z
    .string()
    .transform(onlyDigits)
    .refine((v) => v === "" || v.length === len, `${label} deve ter ${len} dígitos`)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

export const UF_LIST = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
] as const;

export const TAX_REGIMES = ["simples_nacional", "mei", "lucro_presumido", "lucro_real"] as const;

// ---------------------------------------------------------------- empresa
export const companyDataSchema = z.object({
  legalName: z.string().trim().min(2, "Informe a razão social").max(160),
  tradeName: optionalText(160),
  stateRegistration: optionalText(30),
  municipalRegistration: optionalText(30),
  taxRegime: z.enum(TAX_REGIMES).nullable().optional(),
  crt: z.enum(["1", "2", "3", "4"]).nullable().optional(),
  cep: optionalDigits(8, "CEP"),
  street: optionalText(160),
  number: optionalText(20),
  complement: optionalText(80),
  district: optionalText(80),
  city: optionalText(80),
  cityIbgeCode: optionalDigits(7, "Código IBGE"),
  uf: z.enum(UF_LIST).nullable().optional(),
  phone: optionalText(30),
  email: email
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
});
export type CompanyData = z.infer<typeof companyDataSchema>;

export const cnpjSchema = z.string().transform(onlyDigits).refine(isValidCnpj, "CNPJ inválido");

export const companySchema = companyDataSchema.extend({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  cnpj: z.string(),
  status: z.enum(["active", "suspended", "cancelled"]),
  timezone: z.string(),
});
export type Company = z.infer<typeof companySchema>;

// ---------------------------------------------------------------- plataforma
export const createOrganizationSchema = z.object({
  organizationName: z.string().trim().min(2, "Informe o nome da organização").max(160),
  company: companyDataSchema.extend({ cnpj: cnpjSchema }),
  owner: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("self") }),
    z.object({ mode: z.literal("invite"), email }),
  ]),
});
export type CreateOrganizationInput = z.input<typeof createOrganizationSchema>;

/** Novo convite de proprietário emitido pela Plataforma (mesma normalização de e-mail). */
export const issueOwnerInvitationSchema = z.object({ email });
export type IssueOwnerInvitationInput = z.input<typeof issueOwnerInvitationSchema>;

/** Situação do proprietário de uma empresa na tela da Plataforma (nunca traz token). */
export type OwnerInvitationStatus = "pending" | "accepted" | "revoked" | "expired";
export type CompanyOwnerStatus = {
  ownerEmails: string[];
  invitation: {
    id: string;
    email: string;
    status: OwnerInvitationStatus;
    expiresAt: string;
    createdAt: string;
    acceptedAt: string | null;
    revokedAt: string | null;
  } | null;
};

// ---------------------------------------------------------------- /me
export const meResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    fullName: z.string().nullable(),
    isPlatformAdmin: z.boolean(),
    /** conta de usuário (login por usuário + código); null/ausente nas contas de e-mail */
    login: z.object({ username: z.string(), loginCode: z.string() }).nullable().optional(),
    /** senha inicial ou redefinida: a troca é obrigatória antes de usar o sistema */
    mustChangePassword: z.boolean().optional(),
  }),
  companies: z.array(
    z.object({ id: z.string(), name: z.string(), cnpj: z.string(), membershipId: z.string() }),
  ),
  activeCompany: z
    .object({ id: z.string(), name: z.string(), permissions: z.array(z.string()) })
    .nullable(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const updateMeSchema = z.object({
  fullName: z.string().trim().min(2, "Informe seu nome").max(120),
});

// ---------------------------------------------------------------- usuários e papéis
export const setMemberRolesSchema = z.object({
  roleIds: z.array(z.string().uuid()).min(1, "Selecione ao menos um papel").max(20),
});

export const roleInputSchema = z.object({
  name: z.string().trim().min(2, "Informe o nome do papel").max(80),
  description: optionalText(300),
  permissions: z.array(z.string()).max(200),
});
export type RoleInput = z.infer<typeof roleInputSchema>;

// ---------------------------------------------------------------- convites
export const createInvitationSchema = z.object({
  email,
  roleId: z.string().uuid("Selecione o papel"),
});
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;

export const acceptInvitationSchema = z.object({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/, "Link de convite inválido"),
});

export const invitationPreviewSchema = z.object({
  status: z.enum(["pending", "accepted", "revoked", "expired"]),
  emailMatches: z.boolean(),
  companyName: z.string().nullable(),
  roleName: z.string().nullable(),
  expiresAt: z.string(),
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

// ---------------------------------------------------------------- auditoria
export const auditQuerySchema = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  action: z.string().max(80).optional(),
  actorUserId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  before: z.string().datetime({ offset: true }).optional(),
});
